import { useRequiredContext } from "@redotech/react-util/context";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { OrderManagementPermissions } from "@redotech/redo-model/permissions";
import { TeamType } from "@redotech/redo-model/team";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import BarChart10FilledIcon from "@redotech/redo-web/icon-old/bar-chart-10-filled.svg";
import BarChart10Icon from "@redotech/redo-web/icon-old/bar-chart-10.svg";
import { memo, useContext } from "react";
import { useMatch } from "react-router-dom";
import { OrderManagementSuiteStatusesContext } from "../../../../redo-suite/order-management-cloud";

export const AnalyticsItem = memo(function AnalyticsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);

  const { returnsEnabled, packageProtectionEnabled } = useContext(
    OrderManagementSuiteStatusesContext,
  );

  const hasPermissionToViewOrderFulfillment = useHasPermission(
    OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,
  );
  const hasPermissionToViewReturnsClaims = useHasPermission(
    OrderManagementPermissions.VIEW_RETURN_AND_CLAIM_ANALYTICS,
  );
  const hasPermissionToViewReports = useHasPermission(
    OrderManagementPermissions.VIEW_REPORTS,
  );

  const showReturnsClaims =
    (returnsEnabled || packageProtectionEnabled) &&
    hasPermissionToViewReturnsClaims;

  const newOmsEnabled = !!team.settings.outboundLabels?.newOmsEnabled;
  const showOrderFulfillment =
    hasPermissionToViewOrderFulfillment && newOmsEnabled;

  const is3PL = team.teamType === TeamType.FulfillmentService;
  const showReturnsProcessing = is3PL && hasPermissionToViewOrderFulfillment;

  const orderFulfillmentUrlPrefix = routes.analyticsOrderFulfillmentHref;
  const returnsClaimsUrlPrefix = routes.analyticsReturnsClaimsHref;
  const reportsUrlPrefix = routes.analyticsReportsHref;
  const returnsProcessingUrlPrefix = routes.analyticsReturnsProcessingHref;

  const orderFulfillmentSelected = useMatch(`${orderFulfillmentUrlPrefix}/*`);
  const returnsClaimsSelected = useMatch(`${returnsClaimsUrlPrefix}/*`);
  const reportsSelected = useMatch(`${reportsUrlPrefix}/*`);
  const returnsProcessingSelected = useMatch(`${returnsProcessingUrlPrefix}/*`);
  const anyChildSelected =
    orderFulfillmentSelected ||
    returnsClaimsSelected ||
    reportsSelected ||
    returnsProcessingSelected;

  const shouldShowAnalytics =
    showOrderFulfillment ||
    showReturnsClaims ||
    hasPermissionToViewReports ||
    showReturnsProcessing;

  if (!shouldShowAnalytics) {
    return null;
  }

  const defaultRoute = showOrderFulfillment
    ? routes.analyticsOrderFulfillmentHref
    : showReturnsClaims
      ? routes.analyticsReturnsClaimsHref
      : routes.analyticsReportsHref;

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: defaultRoute }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={BarChart10FilledIcon}
      friendlyName="Analytics"
      id="analytics"
      UnfilledIcon={BarChart10Icon}
    >
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.analyticsOrderFulfillmentHref }}
        friendlyName="Order fulfillment"
        hidden={!showOrderFulfillment}
        id="analytics-order-fulfillment"
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.analyticsReportsHref }}
        friendlyName="Reports"
        hidden={!hasPermissionToViewReports}
        id="analytics-reports"
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.analyticsReturnsClaimsHref }}
        friendlyName="Returns & claims"
        hidden={!showReturnsClaims}
        id="analytics-returns-claims"
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.analyticsReturnsProcessingHref }}
        friendlyName="Returns processing"
        hidden={!showReturnsProcessing}
        id="analytics-returns-processing"
      />
    </RedoSecondaryNavItem>
  );
});
